import { useEffect, useState, MouseEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { addMonths } from 'date-fns/addMonths';
import { format } from 'date-fns/format';
import { setMinutes } from 'date-fns/setMinutes';
import { setHours } from 'date-fns/setHours';
import { de as LocaleDe } from 'date-fns/locale/de';
import { fr as LocaleFr } from 'date-fns/locale/fr';
import { es as LocaleEs } from 'date-fns/locale/es';
import { it as LocaleIt } from 'date-fns/locale/it';
import { nl as LocaleNl } from 'date-fns/locale/nl';
import { enUS as LocaleEn } from 'date-fns/locale/en-US';

import { Theme } from '../../../..';

/**
 * @todo: When this component moves layers, these imports shoud be updated
 * to their package aliases (i.e. '@silvertours/front-legacy-shared').
 */
import { useLocale } from '../../../I18n';
import { dateFormats, isoDateTime } from '../../../../strings';

import { ErrorMessage } from '../../../../Ui/ErrorMessage';
import type { DateRange } from './types';
import { DatePickerButton, DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';

import {
  Backdrop,
  TimeRange,
  DatePickerWrapper,
  StyledSubmitButton,
} from './DateRangePicker.styles';

const HOUR_IN_MS = 1000 * 60 * 60;
const INITIAL_MONTHS_MOBILE_CALENDAR = 6;
const MAXIMUM_MONTHS_IN_CALENDAR = 24;

type DateRangePickerProps = {
  from: Date;
  to: Date;
  open?: boolean;
  vertical?: boolean;
  size?: 'small';
  error: string;
  onDatesChange?: (dateRange: DateRange) => void;
};

export const addTimeToDate = (date: Date, hours: number, minutes: number) =>
  setMinutes(setHours(date, hours), minutes);

/** @todo: Move to /entities layer - this is too sophisticated for /shared */
const DateRangePicker = ({
  from,
  to,
  open = false,
  vertical = false,
  size,
  error,
  onDatesChange,
}: DateRangePickerProps) => {
  /**
   * @todo:
   * The translation key should be aligned with the current layer/slice (i.e.
   * 'entities.dateLegacy.dateRangePicker' - once the component has been moved
   * to the /entities layer)
   * */
  const t = useTranslations('features.stageLegacy.stage.searchForm');
  const { language } = useLocale();

  const dateLocale = {
    de: LocaleDe,
    fr: LocaleFr,
    es: LocaleEs,
    it: LocaleIt,
    nl: LocaleNl,
    en: LocaleEn,
  }[language as keyof typeof dateFormats];

  const [isCalendarVisible, setCalendarVisibility] = useState(open);
  const [selectedDates, setSelectedDates] = useState<DateRange>({
    from,
    to,
  });

  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();

  useEffect(() => {
    setSelectedDates({ from, to });
  }, [from, to]);

  const localDateFormats = dateFormats[language as keyof typeof dateFormats];

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setCalendarVisibility(false);
  };

  const handleOnDatesChange = (dateRange: DateRange) => {
    let newDateRange = { from: new Date(), to: new Date() };
    if (
      dateRange.from &&
      dateRange.to &&
      selectedDates.from &&
      selectedDates.to
    ) {
      newDateRange = {
        from: addTimeToDate(
          dateRange.from,
          selectedDates.from.getHours(),
          selectedDates.from.getMinutes(),
        ),
        to: addTimeToDate(
          dateRange.to,
          selectedDates.to.getHours(),
          selectedDates.to!.getMinutes(),
        ),
      };
      if (
        format(dateRange.from, isoDateTime).split('T')[0] ===
        format(dateRange.to, isoDateTime).split('T')[0]
      ) {
        newDateRange = {
          from: addTimeToDate(dateRange.from, 10, 0),
          to: addTimeToDate(dateRange.to, 17, 0),
        };
      }
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange(newDateRange);
      }
      setSelectedDates(newDateRange);
      // keep datepicker on mobile visible after date selection
      setCalendarVisibility(isMobileTouchDevice);
    }
  };

  const handleOnTimeChange = (time: string, type: 'from' | 'to') => {
    const [hours, minutes] = time.split(':');
    if (selectedDates[type]) {
      const dateWithTime = addTimeToDate(
        selectedDates[type]!,
        Number(hours),
        Number(minutes),
      );

      const newDateRange = { ...selectedDates, [type]: dateWithTime };
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange(newDateRange);
      }
      setSelectedDates(newDateRange);
    }
  };

  const getDays = () => {
    if (selectedDates.from && selectedDates.to) {
      // Inspired by https://youmightnotneed.com/date-fns#differenceInDays,
      // because differenceInHours() from date-fns always rounds which is not what we want
      const days =
        ((selectedDates.to as any) - (selectedDates.from as any)) /
        HOUR_IN_MS /
        24;
      return Number.isInteger(days) ? days : Math.ceil(days);
    }
    return 1;
  };

  const getDefaultMonth = () => {
    if (vertical || !selectedDates.from) {
      return undefined;
    }
    return selectedDates.from;
  };

  const getInitialNumberOfMonths = () => {
    if (vertical) {
      return INITIAL_MONTHS_MOBILE_CALENDAR;
    }
    return size === 'small' ? 1 : 2;
  };

  const [numberOfMonths, setNumberOfMonths] = useState(
    getInitialNumberOfMonths(),
  );

  const addMoreMonths = useCallback(() => {
    setNumberOfMonths(Math.min(numberOfMonths + 4, MAXIMUM_MONTHS_IN_CALENDAR));
  }, [numberOfMonths]);

  useEffect(() => {
    // Reset months on viewport change
    setNumberOfMonths(getInitialNumberOfMonths());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical, size]);

  const now = new Date();
  const calendarRange = {
    start: now,
    end: addMonths(now, MAXIMUM_MONTHS_IN_CALENDAR),
  };

  const dayCountText = t('dayCount', { days: getDays() });

  const handleButtonClick = useCallback(() => {
    setCalendarVisibility(!isCalendarVisible);
  }, [isCalendarVisible]);

  return (
    <>
      {isCalendarVisible && (
        // OnKeyDown is necessary for accessibility reasons
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <Backdrop onClick={handleBackdropClick} />
      )}
      <DatePickerButton
        title={t('datePicker')}
        from={from}
        to={to}
        size={size}
        onClick={handleButtonClick}
      />

      <ErrorMessage>{error}</ErrorMessage>

      {isCalendarVisible && (
        <DatePickerWrapper>
          <DatePicker
            onDatesChange={handleOnDatesChange}
            onClose={() => {
              setCalendarVisibility(false);
            }}
            dayCountText={dayCountText}
            disabled={{ before: new Date() }}
            numberOfMonths={numberOfMonths}
            fromMonth={calendarRange.start}
            toMonth={calendarRange.end}
            from={selectedDates.from ? selectedDates.from : undefined}
            to={selectedDates.to ? selectedDates.to : undefined}
            locale={dateLocale}
            defaultMonth={getDefaultMonth()}
            scrollToSelectedMonth={vertical}
            dateFormat={
              vertical
                ? localDateFormats.dayDateLongYear
                : localDateFormats.dayDate
            }
            size={size}
            onScrollToBottom={addMoreMonths}
            vertical={vertical}
          />
          {size !== 'small' && (
            <StyledSubmitButton
              type="button"
              onClick={() => setCalendarVisibility(false)}
            >
              {dayCountText}
            </StyledSubmitButton>
          )}
        </DatePickerWrapper>
      )}

      <TimeRange>
        <TimePicker
          id="depTime"
          type="from"
          label={t('depTime.label')}
          value={
            selectedDates.from
              ? format(selectedDates.from, localDateFormats.timeGeneric)
              : undefined
          }
          onChange={handleOnTimeChange}
        />
        <TimePicker
          id="destTime"
          type="to"
          label={t('destTime.label')}
          value={
            selectedDates.to
              ? format(selectedDates.to, localDateFormats.timeGeneric)
              : undefined
          }
          onChange={handleOnTimeChange}
        />
      </TimeRange>
    </>
  );
};

export { DateRangePicker };
